import { ActionIcon, Alert, Button, Tooltip } from '@mantine/core';
import { IconCamera, IconFileInvoice, IconX } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadInventoryInvoice } from 'src/Hooks/Mutations/useInventoryMutations';

import styles from './InventoryInvoices.module.scss';
import { Props } from './InventoryInvoices.types';

/**
 * The object's invoices. A paper invoice is photographed and filed in Documents
 * (« Factures de l'inventaire »): an invoice always lives there, linked to the object.
 */
const InventoryInvoices = ({ invoices, onAdd, onRemove }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: upload, isLoading } = useUploadInventoryInvoice();

  const addFile = async (file?: File) => {
    if (!file) return;
    setError(null);
    try {
      onAdd(await upload(file));
    } catch {
      setError(
        t('inventory.invoices.uploadError', "La facture n'a pas pu être envoyée. Réessayez.")
      );
    }
  };

  return (
    <div className={styles.container}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        hidden
        onChange={event => {
          addFile(event.currentTarget.files?.[0]);
          event.currentTarget.value = '';
        }}
      />
      {invoices.map(invoice => (
        <div key={invoice.id} className={styles.invoice}>
          <IconFileInvoice size={20} />
          {invoice.url ? (
            <a className={styles.name} href={invoice.url} target="_blank" rel="noreferrer">
              {invoice.name}
            </a>
          ) : (
            <span className={styles.name}>{invoice.name}</span>
          )}
          <Tooltip
            label={t(
              'inventory.invoices.remove',
              'Détacher la facture (elle reste dans Documents)'
            )}
          >
            <ActionIcon
              variant="transparent"
              color="dark"
              size="xl"
              aria-label={t(
                'inventory.invoices.remove',
                'Détacher la facture (elle reste dans Documents)'
              )}
              onClick={() => onRemove(invoice.id)}
            >
              <IconX size={18} />
            </ActionIcon>
          </Tooltip>
        </div>
      ))}
      <Button
        variant="outline"
        size="md"
        leftSection={<IconCamera size={20} />}
        loading={isLoading}
        onClick={() => inputRef.current?.click()}
      >
        {t('inventory.invoices.photograph', 'Photographier la facture')}
      </Button>
      {error && <Alert color="red">{error}</Alert>}
    </div>
  );
};

export default InventoryInvoices;
